/**
 * Programme Schedule DOCX export using docx.js library.
 * Generates Word documents with proper vertical text headers.
 * @module export/schedule-docx
 */

import {
  AlignmentType,
  BorderStyle,
  convertInchesToTwip,
  Document,
  Packer,
  PageOrientation,
  Paragraph,
  Table,
  TableCell,
  TableLayoutType,
  TableRow,
  TextDirection,
  TextRun,
  VerticalAlign,
  WidthType,
} from "docx";

import type { Programme } from "@/types";
import { downloadBlob } from "@/utils/dom";

import type { ScheduleStageData, ScheduleWeeklyTable } from "./schedule-data";
import { buildScheduleData } from "./schedule-data";

export { buildScheduleData } from "./schedule-data";

const HEADER_SHADING = "D9E1F2";
const LABEL_SHADING = "E7E6E6";
const SUBTLE_SHADING = "F2F2F2";
const ASSESSMENT_ONLY_SHADING = "FFF2CC";

// A4 landscape printable width in twips (29.7cm minus 0.5in margins each side).
const SCHEDULE_USABLE_WIDTH = 15400;
// The Module Title column carries the longest text, so it is given a generous
// fixed width; the remaining 28 grid columns share the rest evenly. Combined
// with a fixed table layout this stops the 29-column grid from cramping.
const MODULE_TITLE_COL_WIDTH = 2600;
// Width allotted to the 3 grid columns the Module Title spans in weekly tables.
const WEEKLY_TITLE_WIDTH = 4200;

/** Builds the 29 grid-column widths (twips) for the main schedule table. */
function buildScheduleColumnWidths(): number[] {
  const rest = Math.round((SCHEDULE_USABLE_WIDTH - MODULE_TITLE_COL_WIDTH) / 28);
  return [MODULE_TITLE_COL_WIDTH, ...Array.from({ length: 28 }, () => rest)];
}

/** Builds the grid-column widths (twips) for a weekly table with `numWeeks` week columns. */
function buildWeeklyColumnWidths(numWeeks: number): number[] {
  const titleColEach = Math.round(WEEKLY_TITLE_WIDTH / 3);
  const weekWidth = Math.round(
    (SCHEDULE_USABLE_WIDTH - WEEKLY_TITLE_WIDTH) / Math.max(numWeeks, 1),
  );
  return [
    titleColEach,
    titleColEach,
    titleColEach,
    ...Array.from({ length: numWeeks }, () => weekWidth),
  ];
}

interface CellOpts {
  columnSpan?: number;
  shading?: string;
  bold?: boolean;
  vertical?: boolean;
  small?: boolean;
  width?: number;
  center?: boolean;
}

/** Creates a table cell with common styling. */
function cell(text: string, opts: CellOpts = {}): TableCell {
  const { columnSpan, shading, bold, vertical, small, width, center } = opts;
  const centered = vertical || center;

  return new TableCell({
    children: [
      new Paragraph({
        children: [
          new TextRun({
            text,
            bold,
            size: small ? 12 : 16,
            font: "Calibri",
          }),
        ],
        alignment: centered ? AlignmentType.CENTER : AlignmentType.LEFT,
        spacing: { before: 0, after: 0, line: 240 },
      }),
    ],
    verticalAlign: centered ? VerticalAlign.CENTER : VerticalAlign.TOP,
    margins: {
      top: convertInchesToTwip(0.02),
      bottom: convertInchesToTwip(0.02),
      left: convertInchesToTwip(0.04),
      right: convertInchesToTwip(0.04),
    },
    columnSpan: columnSpan && columnSpan > 1 ? columnSpan : undefined,
    shading: shading ? { fill: shading } : undefined,
    textDirection: vertical ? TextDirection.BOTTOM_TO_TOP_LEFT_TO_RIGHT : undefined,
    width: width ? { size: width, type: WidthType.DXA } : undefined,
  });
}

/**
 * Creates a centred tick cell: a centred ✔ when `active`, otherwise an empty
 * centred cell. Keeps the ✔ marks aligned to the middle of their (often wide,
 * merged) columns so they read clearly against the labelled header above them.
 */
function tickCell(active: boolean, opts: CellOpts = {}): TableCell {
  return cell(active ? "✔" : "", { ...opts, center: true });
}

/** Creates a table row. */
function row(cells: TableCell[], opts: { height?: number } = {}): TableRow {
  const { height } = opts;
  return new TableRow({
    children: cells,
    height: height ? { value: height, rule: "atLeast" } : undefined,
  });
}

/** Generates a schedule table for a version/stage from the schedule model. */
function generateScheduleTable(stageData: ScheduleStageData): Table {
  const rows: TableRow[] = [];

  // Row 1: Title
  rows.push(
    row([
      cell(stageData.title, {
        columnSpan: 29,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ]),
  );

  // Row 2: Provider
  rows.push(
    row([
      cell("Name of Provider:", {
        columnSpan: 3,
        shading: LABEL_SHADING,
        bold: true,
      }),
      cell(stageData.providerName, { columnSpan: 26 }),
    ]),
  );

  // Row 3: Programme Title
  rows.push(
    row([
      cell("Programme Title (Principal)", {
        columnSpan: 3,
        shading: LABEL_SHADING,
        bold: true,
      }),
      cell(stageData.programmeTitle, { columnSpan: 20 }),
      cell("ECTS", { columnSpan: 2, shading: LABEL_SHADING, bold: true }),
      cell(stageData.totalCredits, { columnSpan: 4 }),
    ]),
  );

  // Row 4: Stage
  rows.push(
    row([
      cell("Stage (1,2,3, Award etc)", {
        columnSpan: 3,
        shading: LABEL_SHADING,
        bold: true,
      }),
      cell(stageData.stageName, { columnSpan: 2 }),
      cell("Exit Award Title (if relevant)", {
        columnSpan: 5,
        shading: LABEL_SHADING,
        bold: true,
      }),
      cell(stageData.exitAwardTitle, { columnSpan: 11 }),
      cell("Stage ECTS", { columnSpan: 2, shading: LABEL_SHADING, bold: true }),
      cell(stageData.stageCredits, { columnSpan: 6 }),
    ]),
  );

  // Row 5: Delivery Mode Header
  rows.push(
    row([
      cell("Programme Delivery Mode ✔ one as appropriate.", {
        columnSpan: 3,
        shading: SUBTLE_SHADING,
        bold: true,
      }),
      cell("On-site Face-to-Face", {
        columnSpan: 5,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Blended", { columnSpan: 6, shading: HEADER_SHADING, bold: true }),
      cell("Online", { columnSpan: 6, shading: HEADER_SHADING, bold: true }),
      cell("Apprenticeship", {
        columnSpan: 9,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ]),
  );

  // Row 6: Delivery Mode Values
  rows.push(
    row([
      cell("", { columnSpan: 3 }),
      tickCell(stageData.deliveryTicks.f2f, { columnSpan: 5 }),
      tickCell(stageData.deliveryTicks.blended, { columnSpan: 6 }),
      tickCell(stageData.deliveryTicks.online, { columnSpan: 6 }),
      tickCell(stageData.deliveryTicks.apprenticeship, { columnSpan: 9 }),
    ]),
  );

  // Row 7: Teaching Modalities Header
  rows.push(
    row([
      cell("Teaching and Learning Modalities ✔ one or more as appropriate.", {
        columnSpan: 3,
        shading: SUBTLE_SHADING,
        bold: true,
      }),
      cell("On-site Face-to-Face", {
        columnSpan: 4,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Synchronous Hybrid", {
        columnSpan: 4,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Synchronous Online", {
        columnSpan: 6,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Asynchronous", {
        columnSpan: 4,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Independent", {
        columnSpan: 3,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Work Based", {
        columnSpan: 5,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ]),
  );

  // Row 8: Teaching Modalities Values
  rows.push(
    row([
      cell("", { columnSpan: 3 }),
      tickCell(stageData.modalityTicks.f2f, { columnSpan: 4 }),
      tickCell(stageData.modalityTicks.syncHybrid, { columnSpan: 4 }),
      tickCell(stageData.modalityTicks.syncOnline, { columnSpan: 6 }),
      tickCell(stageData.modalityTicks.asynchronous, { columnSpan: 4 }),
      tickCell(stageData.modalityTicks.independent, { columnSpan: 3 }),
      tickCell(stageData.modalityTicks.workBased, { columnSpan: 5 }),
    ]),
  );

  // Row 9: Assessment Techniques Header
  rows.push(
    row([
      cell("Assessment Techniques Utilised in Stage ✔ one or more as appropriate.", {
        columnSpan: 3,
        shading: SUBTLE_SHADING,
        bold: true,
      }),
      cell("Continuous Assessment", {
        columnSpan: 4,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Invigilated Exam – in person", {
        columnSpan: 4,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Proctored Exam - online", {
        columnSpan: 6,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Project", { columnSpan: 4, shading: HEADER_SHADING, bold: true }),
      cell("Practical Skills Demonstration", {
        columnSpan: 3,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Work Based", {
        columnSpan: 5,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ]),
  );

  // Row 10: Assessment Techniques Values
  rows.push(
    row([
      cell("", { columnSpan: 3 }),
      tickCell(stageData.assessmentTicks.continuous, { columnSpan: 4 }),
      tickCell(stageData.assessmentTicks.invigilated, { columnSpan: 4 }),
      tickCell(stageData.assessmentTicks.proctored, { columnSpan: 6 }),
      tickCell(stageData.assessmentTicks.project, { columnSpan: 4 }),
      tickCell(stageData.assessmentTicks.practical, { columnSpan: 3 }),
      tickCell(stageData.assessmentTicks.workBased, { columnSpan: 5 }),
    ]),
  );

  // Row 11: Modules Section Header
  rows.push(
    row([
      cell("Modules in this stage (add rows as required)", {
        columnSpan: 29,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ]),
  );

  // Row 12: Effort/Assessment Group Headers
  rows.push(
    row([
      cell("", { columnSpan: 8, shading: LABEL_SHADING }),
      cell("Total Student Effort Module (hours)", {
        columnSpan: 10,
        shading: HEADER_SHADING,
        bold: true,
      }),
      cell("Assessment – Allocation of Marks", {
        columnSpan: 11,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ]),
  );

  // Row 13: Column Headers (vertical text)
  rows.push(
    row(
      [
        cell("Module Title", { shading: HEADER_SHADING, bold: true }),
        cell(stageData.teachingPeriodLabel, {
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Mandatory (M) or Elective (E)", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Credits (ECTS)", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Total Hours", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("On-site Face-to-Face", {
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Synchronous", {
          columnSpan: 3,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Asynchronous", {
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Independent", {
          columnSpan: 3,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Work Based", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Continuous Assessment %", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Invigilated Exam – in person %", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Proctored Exam – online %", {
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Project", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Practical Skills Demonstration %", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
        cell("Work Based %", {
          columnSpan: 2,
          shading: HEADER_SHADING,
          bold: true,
          vertical: true,
          small: true,
        }),
      ],
      { height: 1440 },
    ),
  );

  // Module rows
  stageData.modules.forEach((mod) => {
    rows.push(
      row([
        cell(mod.title),
        cell(mod.teachingPeriod),
        cell(mod.mandatoryOrElective, { columnSpan: 2 }),
        cell(mod.credits, { columnSpan: 2 }),
        cell(mod.totalHours, { columnSpan: 2 }),
        cell(mod.effort.classroomHours),
        cell(mod.effort.mentoringHours, { columnSpan: 3 }),
        cell(mod.effort.directedElearningHours),
        cell(mod.effort.independentLearningHours, { columnSpan: 3 }),
        cell(mod.effort.workBasedHours, { columnSpan: 2 }),
        cell(mod.assessment.continuous, {
          columnSpan: 2,
        }),
        cell(mod.assessment.invigilated, {
          columnSpan: 2,
        }),
        cell(mod.assessment.proctored),
        cell(mod.assessment.project, { columnSpan: 2 }),
        cell(mod.assessment.practical, {
          columnSpan: 2,
        }),
        cell(mod.assessment.workBased, {
          columnSpan: 2,
        }),
      ]),
    );
  });

  // Empty rows if no modules
  if (!stageData.hasModuleEntries) {
    for (let i = 0; i < 2; i += 1) {
      rows.push(
        row([
          cell(""),
          cell(""),
          cell("", { columnSpan: 2 }),
          cell("", { columnSpan: 2 }),
          cell("", { columnSpan: 2 }),
          cell(""),
          cell("", { columnSpan: 3 }),
          cell(""),
          cell("", { columnSpan: 3 }),
          cell("", { columnSpan: 2 }),
          cell("", { columnSpan: 2 }),
          cell("", { columnSpan: 2 }),
          cell(""),
          cell("", { columnSpan: 2 }),
          cell("", { columnSpan: 2 }),
          cell("", { columnSpan: 2 }),
        ]),
      );
    }
  }

  return new Table({
    rows,
    width: { size: 100, type: WidthType.PERCENTAGE },
    layout: TableLayoutType.FIXED,
    columnWidths: buildScheduleColumnWidths(),
    borders: {
      top: { style: BorderStyle.SINGLE, size: 4 },
      bottom: { style: BorderStyle.SINGLE, size: 4 },
      left: { style: BorderStyle.SINGLE, size: 4 },
      right: { style: BorderStyle.SINGLE, size: 4 },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 4 },
      insideVertical: { style: BorderStyle.SINGLE, size: 4 },
    },
  });
}

/**
 * Builds reviewer-facing notes listing each module's effort-reconciliation
 * warnings for a stage. The schedule data model attaches these per module when
 * the effort breakdown does not reconcile (PD-096); rendering them here ensures
 * a reviewer sees them rather than the field being silently dropped. A stage
 * whose modules all reconcile yields no paragraphs, keeping the export free of
 * warning noise.
 */
function generateEffortWarningNotes(stageData: ScheduleStageData): Paragraph[] {
  const flaggedModules = stageData.modules.filter((mod) => mod.warnings.length > 0);
  if (flaggedModules.length === 0) {
    return [];
  }

  const paragraphs: Paragraph[] = [
    new Paragraph({
      children: [
        new TextRun({
          text: "Effort Reconciliation Warnings",
          bold: true,
          size: 18,
          font: "Calibri",
        }),
      ],
      spacing: { before: 120, after: 40, line: 240 },
    }),
  ];

  flaggedModules.forEach((mod) => {
    mod.warnings.forEach((warning) => {
      paragraphs.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${mod.title}: `, bold: true, size: 16, font: "Calibri" }),
            new TextRun({ text: warning, size: 16, font: "Calibri" }),
          ],
          spacing: { before: 0, after: 0, line: 240 },
        }),
      );
    });
  });

  return paragraphs;
}

/** Generates weekly schedule tables for a stage from the schedule model, one per teaching period. */
function generateWeeklyTables(weeklyTables: ScheduleWeeklyTable[]): Table[] {
  const tables: Table[] = [];

  weeklyTables.forEach((weekly) => {
    const { numWeeks } = weekly;
    // Total columns: 3 (Module Title spans 3) + numWeeks
    const totalCols = 3 + numWeeks;
    const rows: TableRow[] = [];

    // Row 1: Stage
    rows.push(
      row([
        cell("Stage:", { shading: LABEL_SHADING, bold: true }),
        cell(weekly.stageName),
        cell("", { columnSpan: totalCols - 2 }),
      ]),
    );

    // Row 2: Teaching Period
    rows.push(
      row([
        cell(`${weekly.teachingPeriodLabel}:`, { shading: LABEL_SHADING, bold: true }),
        cell(weekly.teachingPeriodValue),
        cell("", { columnSpan: totalCols - 2 }),
      ]),
    );

    // Row 3: Column headers (Module Title + W1..Wn). Weeks after the
    // teaching period are marked as assessment-only so the teaching duration
    // is not accidentally presented as longer than it is.
    const headerCells: TableCell[] = [
      cell("Module Title", {
        columnSpan: 3,
        shading: HEADER_SHADING,
        bold: true,
      }),
    ];
    for (let i = 1; i <= numWeeks; i += 1) {
      const assessmentOnly = i > weekly.teachingWeeks;
      headerCells.push(
        cell(`W${i}${assessmentOnly ? "*" : ""}`, {
          shading: assessmentOnly ? ASSESSMENT_ONLY_SHADING : HEADER_SHADING,
          bold: true,
          small: true,
        }),
      );
    }
    rows.push(row(headerCells));

    // Module rows
    weekly.modules.forEach((mod) => {
      const activeWeeks = new Set(mod.activeWeeks);
      const moduleCells: TableCell[] = [cell(mod.title, { columnSpan: 3 })];
      for (let i = 1; i <= numWeeks; i += 1) {
        moduleCells.push(
          tickCell(activeWeeks.has(i), {
            shading: i > weekly.teachingWeeks ? ASSESSMENT_ONLY_SHADING : undefined,
          }),
        );
      }
      rows.push(row(moduleCells));
    });

    if (numWeeks > weekly.teachingWeeks) {
      rows.push(
        row([
          cell(`* Assessment-only week after the ${weekly.teachingWeeks}-week teaching period`, {
            columnSpan: totalCols,
            shading: ASSESSMENT_ONLY_SHADING,
            small: true,
          }),
        ]),
      );
    }

    weekly.modules.forEach((mod) => {
      if (mod.unscheduledAssessments.length === 0) {
        return;
      }

      const assessmentSummary = mod.unscheduledAssessments
        .map((assessment) => {
          const title = assessment.title || assessment.type || "Untitled assessment";
          const weighting =
            assessment.weighting == null ? "" : ` (${assessment.weighting}% weighting)`;
          return `${title}${weighting}`;
        })
        .join("; ");

      rows.push(
        row([
          cell(mod.title, {
            columnSpan: 3,
            shading: ASSESSMENT_ONLY_SHADING,
            bold: true,
          }),
          cell(`Outside teaching period, timing not confirmed: ${assessmentSummary}`, {
            columnSpan: numWeeks,
            shading: ASSESSMENT_ONLY_SHADING,
            small: true,
          }),
        ]),
      );
    });

    tables.push(
      new Table({
        rows,
        width: { size: 100, type: WidthType.PERCENTAGE },
        layout: TableLayoutType.FIXED,
        columnWidths: buildWeeklyColumnWidths(numWeeks),
        borders: {
          top: { style: BorderStyle.SINGLE, size: 4 },
          bottom: { style: BorderStyle.SINGLE, size: 4 },
          left: { style: BorderStyle.SINGLE, size: 4 },
          right: { style: BorderStyle.SINGLE, size: 4 },
          insideHorizontal: { style: BorderStyle.SINGLE, size: 4 },
          insideVertical: { style: BorderStyle.SINGLE, size: 4 },
        },
      }),
    );
  });

  return tables;
}

/**
 * Builds the "Version: <label>" heading paragraph that introduces each
 * programme version's schedules. A page break is forced before every version
 * after the first so version groups are visibly separated in the export, and
 * each schedule is labelled with its owning version (PD-107).
 */
function generateVersionHeading(versionLabel: string, pageBreakBefore: boolean): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text: `Version: ${versionLabel}`,
        bold: true,
        size: 28,
        font: "Calibri",
      }),
    ],
    spacing: { before: 0, after: 120, line: 240 },
    pageBreakBefore,
  });
}

/**
 * Generates and downloads a DOCX file for programme schedules.
 */
export async function downloadScheduleDocx(
  data: Programme,
  filename = "programme-schedule.docx",
): Promise<void> {
  const children: (Table | Paragraph)[] = [];

  buildScheduleData(data).stages.forEach((stageData, index) => {
    if (stageData.isFirstStageOfVersion) {
      // Introduce each version with a "Version: <label>" heading, forcing a page
      // break before every version after the first so version groups are clearly
      // separated in the export (PD-107).
      children.push(generateVersionHeading(stageData.versionLabel, index > 0));
    } else if (index > 0) {
      // Page break between stages within a version
      children.push(new Paragraph({ pageBreakBefore: true }));
    }
    children.push(generateScheduleTable(stageData));

    // Per-module effort-reconciliation warnings (PD-096), surfaced as notes so a
    // reviewer sees them rather than the schedule data model field being dropped.
    generateEffortWarningNotes(stageData).forEach((p) => children.push(p));

    // Weekly schedule tables (one per teaching period)
    const weeklyTables = generateWeeklyTables(stageData.weeklyTables);
    weeklyTables.forEach((wt) => {
      children.push(new Paragraph({ pageBreakBefore: true }));
      children.push(wt);
    });
  });

  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            size: {
              orientation: PageOrientation.LANDSCAPE,
              // A4 landscape: 29.7cm (long edge) x 21cm (short edge) in twips.
              width: 16838,
              height: 11906,
            },
            margin: {
              top: "0.5in",
              right: "0.5in",
              bottom: "0.5in",
              left: "0.5in",
            },
          },
        },
        children,
      },
    ],
    styles: {
      default: {
        document: {
          run: {
            font: "Calibri",
            size: 12,
          },
          paragraph: {
            spacing: { before: 0, after: 0, line: 240 },
          },
        },
      },
    },
  });

  const blob = await Packer.toBlob(doc);
  downloadBlob(blob, filename);
}
